// @dynamic
import { WORD_INHERITED_CSS_STYLES_LIST } from '../quill.const';
import { MSOListHelper } from './mso-list.helper';

export class MSOHelper {

    /**
     * Returns true if pasted text contains an identifying tag for Microsoft Word Desktop
     */
    public static isTextFromMSODesktop(pastedText: string): boolean {
        const MSO_DESKTOP_REGEX = /<meta name=Generator content="Microsoft Word/g;

        return !!pastedText.match(MSO_DESKTOP_REGEX);
    }

    /**
     * Finds specific font properties from the mso commented out style tag.
     *
     * @param cssStyleSheet: string - commented out style tag from mso pasted content
     * @param cssSelector: string - css selector for element
     */
    public static getFontPropertiesFromStyleComment(cssStyleSheet: string, cssSelector: string) {
        const result = {};
        const cssSelectorRegEx = new RegExp(`${cssSelector}(.|\n)[^}]*`, 'g');
        const cssSelectorMatch = cssStyleSheet.match(cssSelectorRegEx);
        if (!cssSelectorMatch) {
            return result;
        }
        const matchingStylesForCssSpecifier = cssSelectorMatch[0];

        WORD_INHERITED_CSS_STYLES_LIST.forEach(style => {
            const styleRegex = new RegExp(`\\s${style}(.|\\n)[^;]*`, 'g');
            const styleMatch = matchingStylesForCssSpecifier.match(styleRegex);

            if (styleMatch) {
                const styleKeyValueSplit = styleMatch[0].split(':');
                if (styleKeyValueSplit.length === 2) {
                    const jsStyleName = MSOHelper.convertHyphenedStyleToCamelCase(style);
                    result[jsStyleName] = styleKeyValueSplit[1];
                }
            }
        });

        return result;
    }

    /**
     * If an element does not define an inline style. Selected styles from the class will be added to inline styles
     */
    public static inlineInheritedClassStyles(element: HTMLElement, styleSheetString: string, elementType: string = ''): void {
        let stylesForClass;

        const msoClasses = Array.from(element.classList);
        if (msoClasses.length) {
            const msoClass = msoClasses[0];
            stylesForClass = this.getFontPropertiesFromStyleComment(styleSheetString, `${elementType}.${msoClass}`);
        } else {
            stylesForClass = this.getFontPropertiesFromStyleComment(styleSheetString, elementType);
        }
        if (!Object.keys(stylesForClass)) {
            return;
        }

        WORD_INHERITED_CSS_STYLES_LIST.forEach(style => {
            if (!element.style.getPropertyValue(style)) {
                const jsStyleName = MSOHelper.convertHyphenedStyleToCamelCase(style);
                if (stylesForClass[jsStyleName]) {
                    element.style[jsStyleName] = stylesForClass[jsStyleName];
                }
            }
        });
    }

    public static preprocessContent(pastedDoc: Document) {
        const containerElement = pastedDoc.querySelector('body');
        const styleSheet: string = pastedDoc.querySelector('style').innerHTML;

        const targetElements: HTMLElement[] = Array.from(containerElement.querySelectorAll('p, h1, h2, h3'));
        for (const element of targetElements) {
            if (MSOListHelper.isMSOListItem(element)) {
                continue;
            }
            if (styleSheet) {
                this.inlineInheritedClassStyles(element, styleSheet, element.tagName.toLowerCase());
            }
            this.removeBookmark(element);
            this.convertSpaceIndents(element);
        }
        // remove img that cannot be copied
        containerElement.querySelectorAll('img').forEach(image => image.parentNode.removeChild(image));

        this.removeManualLineBreak(containerElement);
    }

    /**
     * remove manual link break that creates confusion in the text block
     */
    public static removeManualLineBreak(containerElement: HTMLElement): void {
        const lineBreakElements = Array.from(containerElement.querySelectorAll('span br'));
        for (const lineBreak of lineBreakElements) {
            lineBreak.remove();
        }
    }

    /**
     * remove bookmark tags for consistent processing
     */
    public static removeBookmark(element: HTMLElement): void {
        if (element.children.length === 1 &&
            ((element.children[0].tagName === 'A' && element.children[0].hasAttribute('name'))
            || element.children[0].getAttribute('style')?.indexOf('mso-bookmark') > -1)) {
            const childNodes = element.children[0].childNodes;
            element.append(...Array.from(childNodes));
            element.removeChild(element.children[0]);
        }
    }

    public static convertSpaceIndents(element: HTMLElement) {
        if (element.tagName !== 'P' || element.children.length === 0) {
            return;
        }
        const tabCountStyle = element.children[0].getAttribute('style');
        const tabCountIndex = tabCountStyle?.indexOf('mso-tab-count');
        if (tabCountIndex > -1) {
            const tabCount = +tabCountStyle.slice(tabCountIndex)[14];
            element.style.marginLeft = tabCount * 48 + 'px';
            element.removeChild(element.children[0]);
        }
    }

    public static convertHyphenedStyleToCamelCase(style: string): string {
        const styleNameArray = style.split('-');
        const result = styleNameArray.reduce(function(a, b) {
            return a + b.charAt(0).toUpperCase() + b.slice(1);
        });

        return result;
    }
}
